/**
 * PosRedeem — staff-facing camera scanner for gift card vouchers (iter-144)
 *
 * Flow:
 *   1. Open this page on a tablet/phone at the venue
 *   2. Point the camera at a printed voucher's QR code (or a customer's phone
 *      showing the email PDF)
 *   3. The page decodes the QR and validates the gift card via
 *      `POST /gift-cards/validate`
 *   4. Staff types the booking_reference (or booking_id) + their PIN
 *   5. Page calls `POST /admin/bookings/{id}/apply-gift-card`
 *   6. Success card shows the discount applied + new balance
 *
 * No app needed — uses `getUserMedia` (works on iOS Safari + Chrome on Android)
 * and the `jsqr` library for client-side QR decoding.
 *
 * Access: nested under /crm so it inherits the admin/manager auth wall.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import jsQR from 'jsqr';
import { Camera, CheckCircle2, AlertCircle, RefreshCw, ScanLine, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Extract the gift-card code from whatever the QR encoded:
//   - Full URL with `?gift_card=XXX`
//   - Bare code like `GIFT-ABCDEF12`
const extractCode = (raw) => {
  if (!raw) return '';
  try {
    const u = new URL(raw, window.location.origin);
    const p = u.searchParams.get('gift_card');
    if (p) return p.trim().toUpperCase();
  } catch {
    /* not a URL — fall through */
  }
  return String(raw).trim().toUpperCase();
};

export const PosRedeem = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const lastDecodeRef = useRef('');

  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [card, setCard] = useState(null);
  const [bookingRef, setBookingRef] = useState('');
  const [pin, setPin] = useState('');
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  // ── Camera lifecycle ───────────────────────────────────────────
  const stopCamera = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setScanning(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError('');
    setCard(null);
    setResult(null);
    lastDecodeRef.current = '';
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('This browser does not support camera access. Try Chrome or Safari on a mobile device.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setScanning(true);
      tick();
    } catch (e) {
      const msg = e?.name === 'NotAllowedError'
        ? 'Camera permission denied. Tap the lock icon in your browser bar to allow camera access.'
        : e?.name === 'NotFoundError'
          ? 'No camera found on this device.'
          : `Camera unavailable: ${e?.message || e}`;
      setCameraError(msg);
      setScanning(false);
    }
  }, []);

  // ── jsQR decode loop ───────────────────────────────────────────
  const tick = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: 'dontInvert',
    });
    if (code?.data && code.data !== lastDecodeRef.current) {
      lastDecodeRef.current = code.data;
      handleScan(code.data);
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  // Stop camera on unmount
  useEffect(() => () => stopCamera(), [stopCamera]);

  // ── On successful scan: validate the code ──────────────────────
  const handleScan = async (raw) => {
    const code = extractCode(raw);
    if (!code) {
      lastDecodeRef.current = '';
      rafRef.current = requestAnimationFrame(tick);
      return;
    }
    stopCamera();
    try {
      const res = await axios.post(`${API_URL}/api/gift-cards/validate`, { code }, { withCredentials: true });
      setCard(res.data);
      // Pre-fill nothing — staff types booking ref next
      toast.success(`Gift card ${code} found · $${res.data.balance_remaining.toFixed(2)} balance`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Could not validate that code.';
      toast.error(msg);
      // Restart scanner so they can try again
      lastDecodeRef.current = '';
      startCamera();
    }
  };

  // Allow manual entry fallback (typed code)
  const [manualCode, setManualCode] = useState('');
  const manualLookup = async () => {
    if (!manualCode.trim()) return;
    await handleScan(manualCode.trim());
  };

  const applyToBooking = async () => {
    if (!card?.code) return;
    const id = bookingRef.trim();
    if (!id) { toast.error('Enter the booking reference or ID'); return; }
    if (!pin.trim()) { toast.error('Enter your PIN to authorize'); return; }
    setApplying(true);
    try {
      // booking endpoint expects the canonical booking_id. The CRM displays
      // a short reference (BR-XXXXXX). Accept either — try the entered
      // string as-is; if it's a short ref, the backend will 404 and we'll
      // hint the user.
      const bookingId = id.startsWith('booking_') ? id : id;
      const res = await axios.post(
        `${API_URL}/api/admin/bookings/${bookingId}/apply-gift-card`,
        { code: card.code, pin },
        { withCredentials: true },
      );
      setResult({
        applied: res.data?.gift_card?.amount_applied || 0,
        new_balance: res.data?.gift_card?.balance_remaining ?? card.balance_remaining,
        booking_ref: res.data?.booking?.booking_reference || bookingId,
      });
      toast.success('Gift card applied to booking');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply gift card');
    } finally {
      setApplying(false);
    }
  };

  const resetAll = () => {
    setCard(null);
    setBookingRef('');
    setPin('');
    setResult(null);
    setManualCode('');
    setCameraError('');
    lastDecodeRef.current = '';
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            POS Scan-to-Redeem
          </h1>
          <p className="text-sm text-[#6B6B7A]">
            Point the tablet camera at a customer&apos;s gift card QR — we&apos;ll look it up and let you apply
            the credit to a booking in two taps.
          </p>
        </div>
        {(card || result) && (
          <Button onClick={resetAll} variant="outline" className="rounded-full" data-testid="pos-reset">
            <X className="w-4 h-4 mr-1" /> Start over
          </Button>
        )}
      </div>

      {/* Success state */}
      {result && (
        <div className="bg-white rounded-3xl border border-[#06D6A0] p-8 text-center" data-testid="pos-result-success">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#06D6A0]/15 flex items-center justify-center">
            <CheckCircle2 className="w-12 h-12 text-[#06D6A0]" />
          </div>
          <h2 className="text-2xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>
            Credit applied!
          </h2>
          <p className="text-sm text-[#6B6B7A] mt-1">
            <strong className="text-[#06D6A0]">${Number(result.applied).toFixed(2)}</strong> applied to
            booking <strong>{result.booking_ref}</strong>
          </p>
          <p className="text-xs text-[#6B6B7A] mt-2">
            Remaining gift card balance: <strong className="text-[#1E1E24]">${Number(result.new_balance).toFixed(2)}</strong>
          </p>
          <Button onClick={() => { resetAll(); startCamera(); }} className="mt-6 rounded-full bg-[#FF3B6B] text-white" data-testid="pos-scan-next">
            <ScanLine className="w-4 h-4 mr-2" /> Scan next voucher
          </Button>
        </div>
      )}

      {/* Apply step — gift card found, awaiting booking + PIN */}
      {!result && card && (
        <div className="bg-white rounded-3xl border border-[#E8E5DF] p-6 space-y-5" data-testid="pos-apply-step">
          <div className="bg-gradient-to-br from-[#FF3B6B] to-[#FB923C] text-white rounded-2xl p-5 text-center">
            <p className="text-xs uppercase tracking-widest opacity-80">Gift card</p>
            <p className="text-3xl font-bold mt-1 font-mono tracking-wider" data-testid="pos-card-code">{card.code}</p>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <p className="text-[10px] uppercase opacity-80">Face value</p>
                <p className="text-xl font-bold">${Number(card.amount).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase opacity-80">Balance available</p>
                <p className="text-xl font-bold" data-testid="pos-card-balance">${Number(card.balance_remaining).toFixed(2)}</p>
              </div>
            </div>
            {card.expires_at && (
              <p className="text-[11px] opacity-80 mt-3">Expires {String(card.expires_at).slice(0, 10)}</p>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <Label className="text-sm font-semibold">Booking ID or reference *</Label>
              <Input
                value={bookingRef}
                onChange={(e) => setBookingRef(e.target.value)}
                placeholder="booking_… or BR-XXXXXX"
                className="mt-1 rounded-xl border-[#E8E5DF]"
                data-testid="pos-booking-ref"
                autoFocus
              />
              <p className="text-[11px] text-[#6B6B7A] mt-1">
                Find it on the booking detail page in the CRM, or in the customer&apos;s confirmation email.
              </p>
            </div>
            <div>
              <Label className="text-sm font-semibold">Your PIN *</Label>
              <Input
                type="password"
                inputMode="numeric"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="4-digit PIN"
                className="mt-1 rounded-xl border-[#E8E5DF]"
                data-testid="pos-pin"
              />
            </div>
          </div>
          <Button
            onClick={applyToBooking}
            disabled={applying}
            className="w-full bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full h-12 text-base font-semibold"
            data-testid="pos-apply-btn"
          >
            {applying ? 'Applying…' : 'Apply gift card to booking'}
          </Button>
        </div>
      )}

      {/* Scanner */}
      {!card && !result && (
        <div className="bg-white rounded-3xl border border-[#E8E5DF] p-6 space-y-4" data-testid="pos-scanner">
          <div className="relative rounded-2xl overflow-hidden bg-black aspect-[4/3] flex items-center justify-center">
            <video
              ref={videoRef}
              playsInline
              muted
              className={`w-full h-full object-cover ${scanning ? '' : 'opacity-30'}`}
              data-testid="pos-video"
            />
            <canvas ref={canvasRef} className="hidden" />
            {!scanning && !cameraError && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm text-white text-center p-6">
                <div>
                  <Camera className="w-12 h-12 mx-auto mb-2 opacity-90" />
                  <p className="text-sm">Tap below to enable the camera and scan a QR code</p>
                </div>
              </div>
            )}
            {scanning && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-2/3 aspect-square border-4 border-[#FF3B6B] rounded-3xl shadow-[0_0_0_4000px_rgba(0,0,0,0.45)]"></div>
              </div>
            )}
          </div>

          {cameraError && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700" data-testid="pos-camera-error">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{cameraError}</span>
            </div>
          )}

          <div className="flex gap-2">
            {!scanning ? (
              <Button
                onClick={startCamera}
                className="flex-1 bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full h-12 text-base"
                data-testid="pos-start-camera"
              >
                <Camera className="w-5 h-5 mr-2" /> Start camera
              </Button>
            ) : (
              <Button
                onClick={stopCamera}
                variant="outline"
                className="flex-1 rounded-full h-12"
                data-testid="pos-stop-camera"
              >
                <X className="w-5 h-5 mr-2" /> Stop
              </Button>
            )}
            <Button
              onClick={() => { stopCamera(); resetAll(); startCamera(); }}
              variant="outline"
              className="rounded-full h-12 w-12 p-0"
              title="Refresh camera"
              data-testid="pos-restart-camera"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {/* Manual fallback */}
          <div className="bg-[#FFFDF8] border border-[#E8E5DF] rounded-2xl p-4">
            <p className="text-xs font-semibold text-[#6B6B7A] uppercase tracking-widest mb-2">
              Or type the code manually
            </p>
            <div className="flex gap-2">
              <Input
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && manualLookup()}
                placeholder="GIFT-XXXXXXXX"
                className="rounded-xl border-[#E8E5DF] font-mono"
                data-testid="pos-manual-code"
              />
              <Button onClick={manualLookup} variant="outline" className="rounded-full whitespace-nowrap" data-testid="pos-manual-lookup">
                Look up
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PosRedeem;
